export const MAX_OUTLOOK_HEADER_LENGTH = 256 * 1_024;
export const MAX_OUTLOOK_MESSAGE_ID_LENGTH = 16 * 1_024;

const MESSAGE_ID_ATOM = "[a-zA-Z0-9!#$%&'*+\\-/=?^_`{|}~]+";
const MESSAGE_ID_BODY = `${ MESSAGE_ID_ATOM }(?:\\.${ MESSAGE_ID_ATOM })*@${ MESSAGE_ID_ATOM }(?:\\.${ MESSAGE_ID_ATOM })*`;
const MESSAGE_ID_PATTERN = new RegExp(`^<(?:${ MESSAGE_ID_BODY }|\\[${ MESSAGE_ID_BODY }\\])>$`);
const ID_HEADER_NAMES = [ 'message-id', 'references', 'in-reply-to' ];

export type OutlookConversationMode = 'conversation' | 'message' | 'unavailable';
export type OutlookConversationReason = 'root' | 'references' | 'headers-unavailable' | 'invalid-headers' | 'parent-only' | 'missing-message-id';

export interface IOutlookConversationIdentity
{
    readonly mode: OutlookConversationMode;
    readonly anchorId: string | null;
    readonly messageId: string | null;
    readonly reason: OutlookConversationReason;
}

export function parseOutlookMessageId(value: unknown): string | null
{
    if (typeof value !== 'string' || value.length > MAX_OUTLOOK_MESSAGE_ID_LENGTH || /[\r\n]/.test(value))
    {
        return null;
    }
    const id = value.replace(/^[ \t]+|[ \t]+$/g, '');
    return MESSAGE_ID_PATTERN.test(id) ? id : null;
}

function parseIdSequence(value: string): string[] | null
{
    const ids: string[] = [];
    let remaining = value.replace(/^[ \t]+|[ \t]+$/g, '');
    while (remaining)
    {
        const end = remaining.indexOf('>');
        const id = parseOutlookMessageId(remaining.slice(0, end + 1));
        if (!id)
        {
            return null;
        }
        ids.push(id);
        remaining = remaining.slice(end + 1).replace(/^[ \t]+/, '');
    }
    return ids.length ? ids : null;
}

function readIdHeaders(headers: unknown): Map<string, string[]> | null
{
    if (typeof headers !== 'string' || headers.length > MAX_OUTLOOK_HEADER_LENGTH
        || /[^\t\r\n\x20-\x7e\u0080-\uffff]/.test(headers))
    {
        return null;
    }

    const fields = new Map<string, string[]>();
    const lines = headers.replace(/\r\n/g, '\n').split('\n');
    let name: string | null = null;
    let ended = false;
    for (const line of lines)
    {
        if (!line)
        {
            ended = true;
            continue;
        }
        if (ended || line.includes('\r'))
        {
            return null;
        }
        if (/^[ \t]/.test(line))
        {
            if (!name)
            {
                return null;
            }
            const values = fields.get(name);
            if (values)
            {
                values[ values.length - 1 ] += ` ${ line.replace(/^[ \t]+|[ \t]+$/g, '') }`;
            }
            continue;
        }

        const field = /^([!-9;-~]+):[ \t]*(.*)$/.exec(line);
        if (!field)
        {
            return null;
        }
        name = field[ 1 ].toLowerCase();
        if (ID_HEADER_NAMES.includes(name))
        {
            const values = fields.get(name) ?? [];
            values.push(field[ 2 ].replace(/^[ \t]+|[ \t]+$/g, ''));
            fields.set(name, values);
        }
    }
    return fields;
}

function resolveIdField(values: string[] | undefined, single: boolean): string[] | null
{
    if (!values)
    {
        return [];
    }
    const sequences = values.map(parseIdSequence);
    const first = sequences[ 0 ];
    if (!first || (single && first.length !== 1)
        || sequences.some((ids) => !ids || ids.length !== first.length || ids.some((id, index) => id !== first[ index ])))
    {
        return null;
    }
    return first;
}

export function resolveOutlookConversationIdentity(internetMessageId: unknown, headers: unknown): IOutlookConversationIdentity
{
    const hostId = parseOutlookMessageId(internetMessageId);
    const fields = readIdHeaders(headers);
    const headerIds = fields ? resolveIdField(fields.get('message-id'), true) : null;
    const messageId = hostId ?? headerIds?.[ 0 ] ?? null;
    const fallback = (reason: OutlookConversationReason): IOutlookConversationIdentity => ({
        mode: messageId ? 'message' : 'unavailable',
        anchorId: messageId,
        messageId,
        reason,
    });

    if (!fields)
    {
        return fallback(headers == null ? 'headers-unavailable' : 'invalid-headers');
    }
    if (!headerIds || (hostId && headerIds.length > 0 && hostId !== headerIds[ 0 ]))
    {
        return fallback('invalid-headers');
    }
    if (!messageId)
    {
        return fallback('missing-message-id');
    }

    const references = resolveIdField(fields.get('references'), false);
    const parents = resolveIdField(fields.get('in-reply-to'), true);
    if (!references || !parents || references.includes(messageId) || parents.includes(messageId)
        || new Set(references).size !== references.length
        || (references.length > 0 && parents.length > 0 && references[ references.length - 1 ] !== parents[ 0 ]))
    {
        return fallback('invalid-headers');
    }
    if (references.length > 0)
    {
        return { mode: 'conversation', anchorId: references[ 0 ], messageId, reason: 'references' };
    }
    if (parents.length > 0)
    {
        return fallback('parent-only');
    }
    return { mode: 'conversation', anchorId: messageId, messageId, reason: 'root' };
}
